import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Save, RotateCcw, Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, X } from "lucide-react";
import { api, LOGO } from "../lib/api";
import { SITE_SCHEMA, SITE_PAGES } from "../lib/siteSchema";
import { invalidateSiteContent } from "../lib/siteContent";

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const toInputs = (val, item) => (val || []).map((row) => (typeof row === "string" ? { [item[0].key]: row } : row));

function MediaPicker({ onPick, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api("/admin/media").then(setItems).catch(() => setItems([])); }, []);
  const imgs = (items || []).filter((m) => /image/.test(m.mime_type || m.content_type || "") || /\.(png|jpe?g|webp|gif|svg)$/i.test(m.filename || m.url || ""));
  return (
    <div className="se-modal-backdrop" onClick={onClose} data-testid="media-picker">
      <div className="se-modal" onClick={(e) => e.stopPropagation()}>
        <div className="se-modal-head">
          <b>Pilih dari Media Library</b>
          <div style={{ display: "flex", gap: 8 }}>
            <Link to="/admin/media" target="_blank" className="button-ghost small">Upload baru <ExternalLink size={12} /></Link>
            <button className="button-ghost small" onClick={onClose} data-testid="media-picker-close"><X size={14} /></button>
          </div>
        </div>
        {items === null ? <p className="muted">Memuat…</p> : imgs.length === 0 ? <p className="muted">Belum ada gambar. Upload lewat Admin › Media.</p> : (
          <div className="se-media-grid">
            {imgs.map((m) => (
              <button key={m.id} className="se-media-item" onClick={() => onPick(m.url)} data-testid={`media-pick-${m.id}`}>
                <img src={m.url} alt={m.filename} />
                <small>{m.filename}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange, testid }) {
  const [pick, setPick] = useState(false);
  if (field.type === "toggle") {
    return <label className="se-toggle"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} /> {field.label}</label>;
  }
  if (field.type === "textarea") {
    return <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  }
  if (field.type === "image") {
    return (
      <div className="se-image-field">
        <div style={{ display: "flex", gap: 8 }}>
          <input value={value ?? ""} placeholder="https://… atau pilih dari Media" onChange={(e) => onChange(e.target.value)} data-testid={testid} />
          <button type="button" className="button-ghost small" onClick={() => setPick(true)} data-testid={`${testid}-pick`}><ImageIcon size={14} /> Media</button>
        </div>
        {value ? <img src={value} alt="" className="se-image-preview" /> : null}
        {pick && <MediaPicker onPick={(u) => { onChange(u); setPick(false); }} onClose={() => setPick(false)} />}
      </div>
    );
  }
  return <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}

function ListField({ field, value, onChange, testid }) {
  const rows = toInputs(value, field.item);
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const move = (i, d) => { const n = [...rows]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); onChange(n); };
  return (
    <div className="se-list" data-testid={testid}>
      {rows.map((row, i) => (
        <div key={i} className="se-list-row">
          <div className="se-list-fields">
            {field.item.map((f) => (
              <label key={f.key} className={f.type === "toggle" ? "se-inline" : ""}>
                {f.type !== "toggle" && <span>{f.label}</span>}
                <FieldInput field={f} value={row[f.key]} onChange={(v) => set(i, f.key, v)} testid={`${testid}-${i}-${f.key}`} />
              </label>
            ))}
          </div>
          <div className="se-list-actions">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Naik"><ChevronUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="Turun"><ChevronDown size={14} /></button>
            <button type="button" className="danger" onClick={() => onChange(rows.filter((_, j) => j !== i))} title="Hapus" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <button type="button" className="button-ghost small" onClick={() => onChange([...rows, Object.fromEntries(field.item.map((f) => [f.key, f.type === "toggle" ? true : ""]))])} data-testid={`${testid}-add`}>
        <Plus size={14} /> Tambah item
      </button>
    </div>
  );
}

function SectionCard({ page, section, form, setForm }) {
  const keyOf = (f) => `${section.key}.${f.key}`;
  const changed = section.fields.some((f) => form[keyOf(f)] !== undefined && !same(form[keyOf(f)], f.default));
  const reset = () => setForm((prev) => { const n = { ...prev }; section.fields.forEach((f) => delete n[keyOf(f)]); return n; });
  return (
    <details className="se-section" open data-testid={`se-section-${page}-${section.key}`}>
      <summary>
        <span>{section.label}</span>
        <span className="se-section-meta">
          {changed && <em className="se-changed">diubah</em>}
          {changed && <button type="button" className="se-reset" onClick={(e) => { e.preventDefault(); reset(); }} data-testid={`se-reset-${page}-${section.key}`}><RotateCcw size={12} /> Reset default</button>}
        </span>
      </summary>
      <div className="se-fields">
        {section.fields.map((f) => {
          const k = keyOf(f);
          const val = form[k] !== undefined ? form[k] : f.default;
          const onChange = (v) => setForm((prev) => ({ ...prev, [k]: v }));
          const tid = `se-${page}-${section.key}-${f.key}`;
          return (
            <label key={f.key} className={f.type === "toggle" ? "se-inline" : ""}>
              {f.type !== "toggle" && <span>{f.label}</span>}
              {f.type === "list" ? <ListField field={f} value={val} onChange={onChange} testid={tid} /> : <FieldInput field={f} value={val} onChange={onChange} testid={tid} />}
            </label>
          );
        })}
      </div>
    </details>
  );
}

export default function SiteEditor() {
  const [page, setPage] = useState("home");
  const [remote, setRemote] = useState({});
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const schema = SITE_SCHEMA[page];

  const loadAll = () => api("/admin/site/content").then(setRemote).catch(() => {});
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { setForm(remote[page]?.data || {}); setMsg(""); }, [page, remote]);

  const dirty = useMemo(() => !same(form, remote[page]?.data || {}), [form, remote, page]);

  const save = async () => {
    setBusy(true); setMsg("");
    const data = {};
    schema.sections.forEach((s) => s.fields.forEach((f) => {
      const k = `${s.key}.${f.key}`;
      if (form[k] !== undefined && !same(form[k], f.default)) data[k] = form[k];
    }));
    try {
      await api(`/admin/site/content/${page}`, { method: "PUT", data: { data } });
      invalidateSiteContent();
      await loadAll();
      setMsg("Tersimpan. Perubahan langsung tampil di situs.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal menyimpan."); }
    setBusy(false);
  };
  const resetPage = async () => {
    if (!window.confirm(`Kembalikan seluruh konten "${schema.label}" ke default?`)) return;
    setBusy(true);
    try { await api(`/admin/site/content/${page}`, { method: "DELETE" }); invalidateSiteContent(); await loadAll(); setMsg("Dikembalikan ke default."); } catch { setMsg("Gagal reset."); }
    setBusy(false);
  };

  const groups = ["Global", "Halaman"];
  return (
    <div className="se-root" data-testid="site-editor">
      <header className="se-top">
        <Link to="/admin" className="se-back" data-testid="se-back-admin"><ArrowLeft size={16} /> Admin</Link>
        <div className="se-brand"><img src={LOGO} alt="" /> <b>Site Editor</b></div>
        <a href={schema.path} target="_blank" rel="noreferrer" className="button-ghost small" data-testid="se-view-page">Lihat halaman <ExternalLink size={12} /></a>
      </header>
      <div className="se-body">
        <aside className="se-side">
          {groups.map((g) => (
            <div key={g}>
              <small className="se-group">{g}</small>
              {SITE_PAGES.filter((p) => p.group === g).map((p) => (
                <button key={p.key} className={page === p.key ? "active" : ""} onClick={() => setPage(p.key)} data-testid={`se-page-${p.key}`}>
                  {p.label}
                  {Object.keys(remote[p.key]?.data || {}).length > 0 && <i className="se-dot" title="Ada perubahan tersimpan" />}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <main className="se-main">
          <div className="se-head">
            <div>
              <span className="section-kicker">{schema.group.toUpperCase()}</span>
              <h1>{schema.label}</h1>
              <p className="muted">Kosongkan / reset untuk memakai teks default. Gunakan *bintang* untuk aksen warna, Enter untuk baris baru.</p>
              {remote[page]?.updated_at && <small className="muted">Terakhir disimpan {new Date(remote[page].updated_at).toLocaleString("id-ID")} · {remote[page].updated_by}</small>}
            </div>
            <div className="se-actions">
              <button className="button-ghost small" onClick={resetPage} disabled={busy} data-testid="se-reset-page"><RotateCcw size={14} /> Reset halaman</button>
              <button className="button" onClick={save} disabled={busy || !dirty} data-testid="se-save"><Save size={16} /> {busy ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </div>
          {msg && <div className="notice" data-testid="se-msg">{msg}</div>}
          {schema.sections.map((s) => <SectionCard key={s.key} page={page} section={s} form={form} setForm={setForm} />)}
          <div className="se-sticky-save">
            <span>{dirty ? "Ada perubahan belum disimpan" : "Semua tersimpan"}</span>
            <button className="button" onClick={save} disabled={busy || !dirty} data-testid="se-save-bottom"><Save size={16} /> Simpan</button>
          </div>
        </main>
      </div>
    </div>
  );
}
